/**
 * Sibling of `DataTableEndlessPaginated` for the time-offs page. The PAGE owns
 * vertical scroll; this table scrolls only horizontally. The column header
 * pins to the page below the collapsing chrome via `stickyHeaderTopPx`.
 */
import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import type { TableVariants } from '@/components/data-table/data-table';
import {
  DefaultTableEmptyState,
  DefaultTableErrorState,
  DefaultTableLoadingState,
  EmptyStateProps,
  ErrorStateProps,
  LoadingStateProps,
} from '@/components/data-table/data-table-states';

import { DataTablePageScroll } from './data-table-page-scroll';

interface DataTableEndlessPaginatedPageScrollProps<TData> {
  table: Table<TData>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  renderEmptyState?: (props: EmptyStateProps) => React.ReactNode;
  renderErrorState?: (props: ErrorStateProps) => React.ReactNode;
  renderLoadingState?: (props: LoadingStateProps) => React.ReactNode;
  onRowClick?: (row: TData) => void;
  /** Detail-route href for a row — enables CMD/CTRL+click and middle-click "open in new tab". */
  getRowHref?: (row: TData) => string | undefined;
  /** Right-click on a row (e.g. to open a custom context menu). */
  onRowContextMenu?: (row: TData, event: React.MouseEvent) => void;
  getRowClassName?: (row: TData) => string | undefined;
  variant?: TableVariants;
  className?: string | undefined;
  tableId?: string;
  onResizeEnd?: () => void;
  fillLastColumns?: number;
  /** Top offset (px) for the sticky column header. See `DataTablePageScroll`. */
  stickyHeaderTopPx?: number;
}

enum LastRowState {
  LOADING = 'LOADING',
  EMPTY_LIST = 'EMPTY_LIST',
  ERROR = 'ERROR',
  NONE = 'NONE',
}

export const DataTableEndlessPaginatedPageScroll = <TData,>({
  table,
  isLoading,
  hasMore,
  onLoadMore,
  error,
  renderEmptyState,
  renderErrorState,
  renderLoadingState,
  onRowClick,
  getRowHref,
  onRowContextMenu,
  getRowClassName,
  variant,
  className,
  tableId,
  onResizeEnd,
  fillLastColumns,
  stickyHeaderTopPx,
}: DataTableEndlessPaginatedPageScrollProps<TData>) => {
  const rows = table.getRowModel().rows;
  const colSpan = table.getAllColumns().length;
  const { t } = useTranslation('common');
  const sentinelRef = React.useRef<HTMLTableRowElement>(null);

  // The page is the scroll container, so the IO root is the viewport (null).
  // Fires load-more as the user scrolls the page near the bottom of the table.
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoading && !error) {
          onLoadMore();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, error, onLoadMore]);

  let state: LastRowState;
  if (isLoading && !error) state = LastRowState.LOADING;
  else if (rows.length === 0 && !error && !isLoading) state = LastRowState.EMPTY_LIST;
  else if (error) state = LastRowState.ERROR;
  else state = LastRowState.NONE;

  const renderContent = () => {
    const stateProps = { colSpan };
    switch (state) {
      case LastRowState.LOADING:
        return renderLoadingState ? (
          renderLoadingState({ colSpan })
        ) : (
          <DefaultTableLoadingState colSpan={colSpan} />
        );
      case LastRowState.EMPTY_LIST:
        return renderEmptyState ? (
          renderEmptyState(stateProps)
        ) : (
          <DefaultTableEmptyState {...stateProps} />
        );
      case LastRowState.ERROR: {
        const errorMessage = error || t('error.generic');
        return renderErrorState ? (
          renderErrorState({ colSpan, error: errorMessage, retryLoad: onLoadMore })
        ) : (
          <DefaultTableErrorState colSpan={colSpan} error={errorMessage} retryLoad={onLoadMore} />
        );
      }
      case LastRowState.NONE:
        return null;
    }
  };

  const lastRowContent = (
    <>
      {renderContent()}
      {hasMore && !error && (
        <tr ref={sentinelRef} data-sentinel>
          <td colSpan={colSpan} className="h-1 p-0" />
        </tr>
      )}
    </>
  );

  return (
    <DataTablePageScroll
      table={table}
      lastRowChildren={lastRowContent}
      onRowClick={onRowClick}
      getRowHref={getRowHref}
      onRowContextMenu={onRowContextMenu}
      getRowClassName={getRowClassName}
      variant={variant}
      className={className}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
      fillLastColumns={fillLastColumns}
      stickyHeaderTopPx={stickyHeaderTopPx}
    />
  );
};

export type { DataTableEndlessPaginatedPageScrollProps };
